import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import {
  buildImageFormData,
  normalizeProductCreationData,
  normalizeSkuCreationFormData,
} from "@/components/templates/ProductForm/form-data";
import { normalizeStockMovementData } from "@/components/templates/StockMovementModal/form-data";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useCreateProductImage from "@/hooks/requests/product-images/use-create-product-image";
import useCreateSKU from "@/hooks/requests/sku/use-create-sku";
import useLoading from "@/hooks/use-loading";
import useMutate from "@/hooks/use-mutate";
import { ISkuInput } from "@/interfaces/sku.interface";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";

import useCreateProduct from "./use-create-product";
import useCreateStockMovement from "../stock/use-create-stock";
import { useSaveLinkedCategories } from "../linked-product-category";

type useCreateProductWithSkusProps = {
  messages: ProductFormPageMessages;
  customerId: string | null;
};

const useCreateProductWithSkus = ({ messages, customerId }: useCreateProductWithSkusProps) => {
  const { startLoading, endLoading, isLoading, loadingIcon } = useLoading();
  const { create: createSku } = useCreateSKU();
  const { create: createStockMovement } = useCreateStockMovement();
  const { create: createProduct } = useCreateProduct();
  const {
    create: createImg,
    isLoading: isLoadingCreateImg,
    loadingIcon: loadingCreateIconImg,
  } = useCreateProductImage();
  const { saveCategories } = useSaveLinkedCategories();
  const { mutate } = useMutate();
  const router = useNavigate();
  const { lang } = useLanguage();

  const handleCreate = async (_formData: ProductFormData, showSuccessMessage = true) => {
    startLoading();

    try {
      const formData = normalizeProductCreationData(_formData);

      const response = await createProduct({
        formData: { ...formData, productImages: null },
        successMessage: showSuccessMessage ? messages.productCreateSuccess : "",
        errorMessage: messages.productCreateError,
      });

      if (response && _formData.productImages) {
        for (const image of _formData.productImages) {
          if ("file" in image) {
            const imageFormData = buildImageFormData(
              image as import("@/interfaces/product.interface").ImageData,
              response.id.toString()
            );
            try {
              await createImg({
                formData: imageFormData,
                errorMessage: messages.imageCreateError,
              });
            } catch (e) {
              // Continue creating SKUs even if image upload fails
            }
          }
        }

        // Criação dos SKUs
        for (const skuData of _formData.skus) {
          const _customerId = Number(customerId);
          const normalizedSkuData = normalizeSkuCreationFormData(
            skuData,
            response,
            _customerId,
            false
          );

          const createSkuResponse = await createSku({
            formData: normalizedSkuData,
            errorMessage: messages.skuCreateError,
          });

          await handleStockMovementCreation(skuData, createSkuResponse.id);
        }
      }

      // Salvar categorias selecionadas se houver
      if (response && _formData.selectedCategories.length > 0) {
        await saveCategories({
          data: {
            productId: response.id,
            categoryIds: _formData.selectedCategories,
          },
        });
      }

      mutate(SWR_KEYS.getProductById);
      router(ROUTE_URLS.products(lang));
      return response;
    } finally {
      endLoading();
    }
  };

  const handleStockMovementCreation = async (_formData: ISkuInput, skuId: number) => {
    const stockMovementData = _formData.stockMovement;

    if (stockMovementData) {
      const normalizedStockMovementData = normalizeStockMovementData(stockMovementData, skuId);
      await createStockMovement({
        formData: normalizedStockMovementData,
        errorMessage: messages.errorCreateStockMessage,
      });
    }
  };

  return {
    handleCreate,
    isLoading,
    loadingIcon,
    isLoadingCreateImg,
    loadingCreateIconImg,
  };
};

export default useCreateProductWithSkus;
